type Bytes = Uint8Array | { length: number; [index: number]: number };

function byteAt(buf: Bytes, offset: number): number {
  return buf[offset] ?? 0;
}

export function readUInt8(buf: Bytes, offset: number): number {
  return byteAt(buf, offset);
}

export function readUInt16LE(buf: Bytes, offset: number): number {
  return byteAt(buf, offset) | (byteAt(buf, offset + 1) << 8);
}

export function readInt16LE(buf: Bytes, offset: number): number {
  const value = readUInt16LE(buf, offset);
  return value > 0x7fff ? value - 0x10000 : value;
}

export function readUInt32LE(buf: Bytes, offset: number): number {
  return (
    (byteAt(buf, offset) |
      (byteAt(buf, offset + 1) << 8) |
      (byteAt(buf, offset + 2) << 16) |
      (byteAt(buf, offset + 3) << 24)) >>>
    0
  );
}

export function readInt32LE(buf: Bytes, offset: number): number {
  return readUInt32LE(buf, offset) | 0;
}

export function readFloatLE(buf: Bytes, offset: number): number {
  const slice =
    buf instanceof Uint8Array
      ? buf.subarray(offset, offset + 4)
      : Uint8Array.from({ length: 4 }, (_, index) => byteAt(buf, offset + index));
  return new DataView(slice.buffer, slice.byteOffset, slice.byteLength).getFloat32(0, true);
}

/** Decode fixed-width AIM ASCII without Buffer#toString (broken on React Native). */
export function readLatin1String(buf: Bytes, offset: number, length: number): string {
  const end = Math.min(offset + length, buf.length);
  let value = "";
  for (let index = offset; index < end; index += 1) {
    const byte = byteAt(buf, index);
    if (byte === 0) {
      break;
    }
    value += String.fromCharCode(byte);
  }
  return value.trim();
}

export function readLatin1Range(buf: Bytes, start: number, end: number): string {
  let value = "";
  for (let index = start; index < end; index += 1) {
    value += String.fromCharCode(byteAt(buf, index));
  }
  return value;
}

export function bytesMatchAscii(buf: Bytes, offset: number, text: string): boolean {
  if (offset + text.length > buf.length) {
    return false;
  }

  for (let index = 0; index < text.length; index += 1) {
    if (byteAt(buf, offset + index) !== text.charCodeAt(index)) {
      return false;
    }
  }

  return true;
}
